<template>
  <BbPopup v-model="isOpened" :showCloseIcon="true" contentClass="create-collection-dialog">
    <template #popup-header>
      <TextBody tag="span" :i18n-key="modalTitle" :body-type="3" weight="medium"></TextBody>
    </template>
    <div class="info-container">
      <BbInput
        v-model="localCollection.collectionName"
        label="Title"
        size="small"
        :placeholder="t('collections.createPopup.placeholderTitle')"
        :rules="rules"
      >
        <template #append-inner>
          <span>{{ charactersLength + '/65' }}</span>
        </template>
      </BbInput>
      <BbTextarea
        v-model="localCollection.collectionDescription"
        label="Description"
        :placeholder="t('collections.createPopup.placeholderDescription')"
        :rows="3"
        :auto-grow="true"
      >
      </BbTextarea>
    </div>
    <template #popup-actions>
      <BbBtn type="secondary" @click="onCancel">
        <TextBody i18n-key="common.cancel"></TextBody>
      </BbBtn>
      <BbBtn color="#6222e3" @click="handleCollection" :is-disabled="hasError || isDataLoading">
        <BbIcon v-if="isDataLoading" size="small" name="spin" />
        <TextBody :i18n-key="isEditMode ? 'common.update' : 'collections.newCollection'"></TextBody>
      </BbBtn>
    </template>
  </BbPopup>
</template>

<script>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';
import TextBody from '~/components/common/TextBody';
import useCollections from '~/composables/useCollections';
import useModals from '~/composables/useModals';

export default {
  components: { TextBody },

  setup() {
    const { t } = useI18n();
    const { createCollection, updateCollection } = useCollections();
    const { setCreateCollectionPopup, createCollectionPopupState } = useModals();
    const isDataLoading = ref(false);
    const localCollection = reactive({
      collectionName: '',
      collectionDescription: '',
    });

    const isOpened = computed({
      get: () => createCollectionPopupState.value.open,
      set: (val) => setCreateCollectionPopup({ open: val, collection: {} }),
    });

    const isEditMode = computed(
      () => createCollectionPopupState.value.collection?.collectionId,
    );

    const charactersLength = computed(() => localCollection.collectionName.length);

    const modalTitle = computed(() => isEditMode.value
      ? 'collections.createPopup.modalTitleUpdate'
      : 'collections.createPopup.modalTitleCreate');

    const rules = [
      (v) => !!v || t('collections.createPopup.titleEmptyError'),
      (v) => v.length <= 65 || t('collections.createPopup.titleMaxLengthError'),
    ];

    const hasError = computed(
      () => localCollection.collectionName.length <= 0 || localCollection.collectionName.length > 65,
    );

    const onCancel = () => setCreateCollectionPopup({ open: false, collection: {} });
    const handleCollection = async () => {
      isDataLoading.value = true;
      isEditMode.value
        ? await updateCollection({
            ...localCollection,
            collectionId: createCollectionPopupState.value.collection.collectionId,
          })
        : await createCollection(localCollection);
      isDataLoading.value = false;
      setCreateCollectionPopup({ open: false, collection: {} });
    };

    // Watch for changes on collection prop to update local reactive state
    watch(
      () => createCollectionPopupState.value.collection,
      (newCollection) => {
        localCollection.collectionName = newCollection?.collectionName || '';
        localCollection.collectionDescription = newCollection?.collectionDescription || '';
      },
      { deep: true, immediate: true },
    );

    return {
      charactersLength,
      localCollection,
      isDataLoading,
      isEditMode,
      isOpened,
      handleCollection,
      hasError,
      modalTitle,
      onCancel,
      rules,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.info-container {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;

  .bb-input {
    :deep(.bb-input__label) {
      color: $bb-neutral-1;
      margin-bottom: $bb-spacing-1;
    }

    :deep(.v-input .v-input__control) {
      background-color: $bb-neutral-0;
    }

    :deep(.v-input .v-input__control .v-field .v-field__append-inner) {
      color: $bb-neutral-4;
      @include bb-regular-text-styles('md');
    }
  }

  .bb-textarea {
    :deep(.v-input) {
      background-color: $bb-neutral-0;
    }

    :deep(.bb-textarea__label) {
      color: $bb-neutral-1;
      margin-bottom: $bb-spacing-1;
    }
  }
}
</style>
